import { useState } from "react";
import { authPost, errorMessage, staleLinkMessage } from "../lib/authApi.ts";

// The verification landing. A good link lands on `/` signed in; the server
// redirects a stale one here with the auth library's `error` code.
// See ADR-0042.

type VerifiedPageProps = {
  error: string | null;
};

/**
 * Explains a stale verification link and offers to send a fresh one. With no
 * `error` it says the address is verified and links to the Projects grid.
 * Rendered by the public entry at `/verified`.
 */
export function VerifiedPage({ error }: VerifiedPageProps): JSX.Element {
  if (error === null) {
    return (
      <div className="auth-card">
        <h1>You're signed in</h1>
        <p className="lede">
          Your address is verified and your Organisation is ready. Add your
          first Project from the grid.
        </p>
        <div className="btn-row">
          <a className="btn primary lg block" href="/">
            Continue
          </a>
        </div>
      </div>
    );
  }
  return <StaleVerificationLink code={error} />;
}

function StaleVerificationLink({ code }: { code: string }): JSX.Element {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [err, setErr] = useState<string | null>(null);

  async function resend(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    setState("sending");
    try {
      await authPost("/send-verification-email", { email, callbackURL: "/" });
      setState("sent");
    } catch (x) {
      setErr(errorMessage(x));
      setState("idle");
    }
  }

  return (
    <div className="auth-card">
      <h1>That link didn't work</h1>
      <div className="notice warn" role="alert">
        <div>{staleLinkMessage(code)}</div>
      </div>
      {state === "sent" ? (
        <div className="notice info" role="status">
          <div>If that address has an unverified account, a fresh link is on its way.</div>
        </div>
      ) : (
        <form onSubmit={resend} aria-label="Re-send verification">
          <div className="field">
            <label htmlFor="verified-email">Your email</label>
            <input
              id="verified-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {err ? (
            <div className="notice bad" role="alert">
              <div>{err}</div>
            </div>
          ) : null}
          <div className="btn-row">
            <button type="submit" className="btn primary lg block" disabled={state === "sending"}>
              {state === "sending" ? "Sending…" : "Send a new link"}
            </button>
          </div>
        </form>
      )}
      <p className="hint">
        Already verified? <a href="/login">Sign in</a>.
      </p>
    </div>
  );
}
